/** One run of lines that survived, was dropped, or was introduced between two texts. */
export interface LineDiffSegment {
  readonly kind: 'same' | 'removed' | 'added'
  readonly lines: readonly string[]
}

/** How many lines each side may have before the diff degrades to a whole-text replacement. */
const MAX_LINES = 400

function longestCommonSubsequence(before: readonly string[], after: readonly string[]): number[][] {
  const table: number[][] = Array.from({ length: before.length + 1 }, () =>
    Array.from({ length: after.length + 1 }, () => 0),
  )

  for (let i = before.length - 1; i >= 0; i -= 1) {
    for (let j = after.length - 1; j >= 0; j -= 1) {
      table[i]![j] =
        before[i] === after[j]
          ? table[i + 1]![j + 1]! + 1
          : Math.max(table[i + 1]![j]!, table[i]![j + 1]!)
    }
  }

  return table
}

function push(segments: LineDiffSegment[], kind: LineDiffSegment['kind'], line: string): void {
  const tail = segments[segments.length - 1]
  if (tail?.kind === kind) {
    segments[segments.length - 1] = { kind, lines: [...tail.lines, line] }
    return
  }
  segments.push({ kind, lines: [line] })
}

/** A line-level diff of `before` against `after`, as consecutive runs; past `MAX_LINES` the texts are reported as one removal and one addition. */
export function diffLines(before: string, after: string): LineDiffSegment[] {
  if (before === after) {
    return before === '' ? [] : [{ kind: 'same', lines: before.split('\n') }]
  }

  const beforeLines = before === '' ? [] : before.split('\n')
  const afterLines = after === '' ? [] : after.split('\n')

  if (beforeLines.length > MAX_LINES || afterLines.length > MAX_LINES) {
    const segments: LineDiffSegment[] = []
    if (beforeLines.length > 0) segments.push({ kind: 'removed', lines: beforeLines })
    if (afterLines.length > 0) segments.push({ kind: 'added', lines: afterLines })
    return segments
  }

  const table = longestCommonSubsequence(beforeLines, afterLines)
  const segments: LineDiffSegment[] = []

  let i = 0
  let j = 0
  while (i < beforeLines.length && j < afterLines.length) {
    if (beforeLines[i] === afterLines[j]) {
      push(segments, 'same', beforeLines[i]!)
      i += 1
      j += 1
    } else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
      push(segments, 'removed', beforeLines[i]!)
      i += 1
    } else {
      push(segments, 'added', afterLines[j]!)
      j += 1
    }
  }

  while (i < beforeLines.length) {
    push(segments, 'removed', beforeLines[i]!)
    i += 1
  }
  while (j < afterLines.length) {
    push(segments, 'added', afterLines[j]!)
    j += 1
  }

  return segments
}

/** How many lines the diff drops and introduces - the headline a caller states before the detail. */
export function summarizeDiff(segments: readonly LineDiffSegment[]): {
  removed: number
  added: number
} {
  let removed = 0
  let added = 0
  for (const segment of segments) {
    if (segment.kind === 'removed') removed += segment.lines.length
    if (segment.kind === 'added') added += segment.lines.length
  }
  return { removed, added }
}
